import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { LinkElement } from '@els/els-react--link-element';

import './ReactionStageDetail.scss';

/* eslint-disable react/no-array-index-key */

function last(index, array) {
  return index === array.length - 1;
}

function ReactionStageDetailComponent(props) {
  const [experimentalProcedureExpanded, setExperimentalProcedureExpanded] = useState(false);
  const { metadata, stage, stageIndex, hasMultipleStages, isCIDatabase, index, leadingContent } = props;

  const substanceClick = useCallback((e, item, currentMetadata) => {
    e.preventDefault();
    ReactReactionDetailsHelper.goToContext('substances', item, currentMetadata);
  }, []);

  const getReactantsTemlate = () =>
    stage.reactants?.map((reactant, reactantIndex, reactants) => (
      <span className="stage-reactants" key={reactantIndex}>
        {reactant.id ? (
          <LinkElement
            href="#"
            inline
            navigation
            onClick={(e) => {
              e.preventDefault();
              substanceClick(e, reactant, metadata);
            }}
            data-e2e="reaction-stage-reactant-link"
            mode="escape">
            <InnerHTML mode="escape" tag="span" html={reactant.name} />
          </LinkElement>
        ) : (
          <InnerHTML tag="span" html={reactant.name} mode="escape" />
        )}
        {!last(reactantIndex, reactants) && <>;&nbsp;</>}
      </span>
    ));

  const getReagentsTemplate = () => (
    <>
      <b> With </b>
      {stage.reagents?.map((reagent, reagentIndex, reagents) => (
        <span className="stage-reagents" key={reagentIndex}>
          {reagent.id ? (
            <LinkElement
              data-e2e="stage-reagent-link"
              href="#"
              inline
              navigation
              onClick={(e) => {
                e.preventDefault();
                substanceClick(e, reagent, metadata);
              }}
              mode="escape">
              <InnerHTML mode="escape" tag="span" html={reagent.name} />
            </LinkElement>
          ) : (
            <InnerHTML tag="span" html={reagent.name} mode="escape" />
          )}
          {!last(reagentIndex, reagents) && <>;&nbsp;</>}
        </span>
      ))}
    </>
  );

  const getCatalystsTemplate = () => (
    <>
      {stage.reactants || stage.reagents ? <>; </> : null}
      {stage.catalysts?.map((catalyst, catalystIndex, catalysts) => (
        <span className="stage-catalyst" key={catalystIndex}>
          {catalyst.id ? (
            <LinkElement
              inline
              navigation
              href="#"
              data-e2e="stage-catalyst-link"
              onClick={(e) => {
                e.preventDefault();
                substanceClick(e, catalyst, metadata);
              }}
              mode="escape">
              <InnerHTML mode="escape" tag="span" html={catalyst.name} />
            </LinkElement>
          ) : (
            <InnerHTML tag="span" html={catalyst.name} mode="escape" />
          )}
          {!last(catalystIndex, catalysts) && <>;&nbsp;</>}
        </span>
      ))}
    </>
  );

  const getSolventsTemplate = () => (
    <>
      <b> In </b>
      {stage.solvents?.map((solvent, solventIndex, solvents) => (
        <span className="stage-solvents" key={solventIndex}>
          {solvent.id ? (
            <LinkElement
              data-e2e="stage-solvent-link"
              href="#"
              inline
              navigation
              onClick={(e) => {
                e.preventDefault();
                substanceClick(e, solvent, metadata);
              }}
              mode="escape">
              <InnerHTML mode="escape" tag="span" html={solvent.name} />
            </LinkElement>
          ) : (
            <InnerHTML tag="span" html={solvent.name} mode="escape" />
          )}
          {!last(solventIndex, solvents) && <>;&nbsp;</>}
        </span>
      ))}
    </>
  );

  const getCIConditions = () => (
    <span className="conditions">
      {stage.atmosphere && (
        <>
          <InnerHTML tag="span" html={stage.atmosphere} mode="escape" />
          {'; '}
        </>
      )}
      {stage.reflux && (
        <>
          <InnerHTML tag="span" html={stage.reflux} mode="escape" />
          {'; '}
        </>
      )}
      {stage.totalVolume && (
        <>
          <InnerHTML tag="span" html={stage.totalVolume} mode="escape" />
          {'; '}
        </>
      )}
      {stage.molarity && (
        <>
          {' '}
          Molarity =
          <InnerHTML tag="span" html={stage.molarity} mode="escape" />
          mol/l
          {'; '}
        </>
      )}
    </span>
  );

  const getExperimentalProcedure = () => {
    const id = `reaction-stage-procedure-${index}-${stageIndex}`;
    return (
      <div className="stage-experimental-procedure">
        <ToggleButton
          ariaControls={id}
          expanded={experimentalProcedureExpanded}
          toggleExpand={() => setExperimentalProcedureExpanded(!experimentalProcedureExpanded)}
          expandedLabel="Experiment Procedure"
          collapsedLabel="Experiment Procedure"
          isViewPreview={getValueByNs(props, 'metadata.isViewPreview')}
          className="els-btn-xs e2e-experiment-procedure"
        />
        {experimentalProcedureExpanded && (
          <div id={id} aria-expanded={experimentalProcedureExpanded}>
            <InnerHTML tag="div" className="stage-experimental-procedure--trust-html" html={stage.experimentalProcedure} extendedTags />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="stages-row">
      {leadingContent}
      {hasMultipleStages && <b>Stage #{stageIndex + 1}: </b>}
      {stage.reactants && getReactantsTemlate()}
      {stage.reagents && getReagentsTemplate()}
      {stage.catalysts && getCatalystsTemplate()}
      {stage.solvents && getSolventsTemplate()}
      <span className="conditions">
        {stage.temperature && (
          <>
            {' '}
            at <InnerHTML tag="span" html={stage.temperature} mode="escape" />
            &#8451;
            {'; '}
          </>
        )}
        {stage.pressure && (
          <>
            {' '}
            under <InnerHTML tag="span" html={stage.pressure} mode="escape" /> Torr
            {'; '}
          </>
        )}
        {stage.duration && (
          <>
            {' '}
            for <InnerHTML tag="span" html={stage.duration} mode="escape" />h{'; '}
          </>
        )}
        {stage.phValue && (
          <>
            {' '}
            pH=
            <InnerHTML tag="span" html={stage.phValue} mode="escape" />
            {'; '}
          </>
        )}
        {stage.solidPhase && (
          <>
            <InnerHTML tag="span" html={` ${stage.solidPhase}`} mode="escape" />
            {'; '}
          </>
        )}
        {stage.subjects &&
          stage.subjects?.map((subject, subjectIndex) => (
            <React.Fragment key={subjectIndex}>
              <InnerHTML tag="span" key={subjectIndex} html={` ${subject}`} mode="escape" />
              {'; '}
            </React.Fragment>
          ))}
        {stage.prototypes &&
          stage.prototypes?.map((prototype, prototypeIndex) => (
            <React.Fragment key={prototypeIndex}>
              <InnerHTML tag="span" key={prototypeIndex} html={` ${prototype}`} mode="escape" />
              {'; '}
            </React.Fragment>
          ))}
        {stage.name && (
          <>
            <InnerHTML tag="span" html={` ${stage.name}`} mode="escape" />
            {'; '}
          </>
        )}
        {stage.types &&
          stage.types?.map((type, typeIndex) => (
            <React.Fragment key={typeIndex}>
              <InnerHTML tag="span" key={typeIndex} html={` ${type}`} mode="escape" />
              {'; '}
            </React.Fragment>
          ))}
        {stage.conditions &&
          stage.conditions?.map((condition, conditionIndex) => (
            <React.Fragment key={conditionIndex}>
              <InnerHTML tag="span" key={conditionIndex} html={` ${condition}`} mode="escape" />
              {'; '}
            </React.Fragment>
          ))}
        {stage.comments &&
          stage.comments?.map((comment, commentIndex) => (
            <React.Fragment key={commentIndex}>
              <InnerHTML tag="span" key={commentIndex} html={` ${comment}`} mode="escape" />
              {'; '}
            </React.Fragment>
          ))}
      </span>
      {isCIDatabase && getCIConditions()}
      {isCIDatabase && getExperimentalProcedure()}
    </div>
  );
}

ReactionStageDetailComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  stage: PropTypes.object.isRequired,
  stageIndex: PropTypes.number.isRequired,
  hasMultipleStages: PropTypes.bool,
  isCIDatabase: PropTypes.bool,
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadingContent: PropTypes.node,
};

export default ReactionStageDetailComponent;
